import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { mabarService, PublicMabarEvent, isEventPast } from '@/services/mabarService';
import { wilayahService, KotaOption } from '@/services/wilayahService';
import { MabarCardSkeleton } from '@/components/client/skeleton/ClientSkeletons';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';

const LocationSearchDropdown: React.FC<{
  value: string;
  onChange: (cityName: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [locationOptions, setLocationOptions] = useState<KotaOption[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!query || query.trim().length < 3) {
      setLocationOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearchingLocation(true);
      const res = await wilayahService.searchKota(query);
      setLocationOptions(res);
      setIsSearchingLocation(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative w-full" ref={ref}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full h-11 pl-8 pr-6 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-semibold text-[#0E1726] focus:outline-none focus:border-[#FC480D] shadow-2xs flex items-center justify-between cursor-pointer truncate transition-colors"
      >
        <Icon
          icon="ph:map-pin-bold"
          className="w-4 h-4 text-gray-400 absolute left-2.5 pointer-events-none"
        />
        <span className="truncate">{value || 'Lokasi'}</span>
        <Icon
          icon="ph:caret-down-bold"
          className="w-3.5 h-3.5 text-gray-400 absolute right-2 pointer-events-none"
        />
      </button>

      {/* Floating Dropdown Card */}
      {open && (
        <div className="absolute right-0 top-12 w-64 md:w-72 bg-white rounded-2xl border border-[#E0E6ED] shadow-xl z-30 p-2 space-y-2 animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Search Input Field */}
          <div className="relative flex items-center">
            <Icon
              icon="ph:magnifying-glass-bold"
              className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none"
            />
            <input
              type="text"
              autoFocus
              placeholder="Cari Kota/Kabupaten (min. 3 huruf)..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-8 rounded-xl bg-gray-50 border border-gray-200 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D]"
            />
            <div className="absolute right-2.5 flex items-center">
              {isSearchingLocation ? (
                <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-[#FC480D]" />
              ) : query ? (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="text-gray-400 hover:text-gray-600 text-xs font-bold"
                >
                  ✕
                </button>
              ) : null}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-52 overflow-y-auto space-y-0.5 scrollbar-thin divide-y divide-gray-50">
            {/* Option: Semua Lokasi */}
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium transition-colors flex items-center justify-between ${
                !value
                  ? 'bg-orange-50 text-[#FC480D] font-bold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>Semua Lokasi</span>
              {!value && <Icon icon="ph:check-bold" className="w-3.5 h-3.5" />}
            </button>

            {query.trim().length > 0 && query.trim().length < 3 ? (
              <div className="py-3 px-3 text-center text-[11px] text-gray-400 font-medium">
                Ketik minimal 3 huruf untuk mencari kota...
              </div>
            ) : isSearchingLocation ? (
              <div className="py-4 text-center text-xs text-gray-400 font-medium flex items-center justify-center gap-2">
                <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin text-[#FC480D]" />
                <span>Mencari lokasi...</span>
              </div>
            ) : locationOptions.length === 0 && query.trim().length >= 3 ? (
              <div className="py-3 px-3 text-center text-[11px] text-gray-400 font-medium">
                Kota tidak ditemukan
              </div>
            ) : (
              locationOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.nama);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-xl text-xs transition-colors flex items-center justify-between ${
                    value === opt.nama
                      ? 'bg-orange-50 text-[#FC480D] font-bold'
                      : 'hover:bg-orange-50/60 text-[#0E1726]'
                  }`}
                >
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="font-bold text-xs text-[#0E1726] truncate">{opt.nama}</div>
                    <div className="text-gray-400 text-[10px] truncate">{opt.namaLengkap}</div>
                  </div>
                  {value === opt.nama && (
                    <Icon icon="ph:check-bold" className="w-3.5 h-3.5 text-[#FC480D] shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Date filter pill types
type DateFilterPill = 'today' | 'weekend' | 'all' | 'custom';

const indonesianMonths = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

const dayNamesIndonesian = ['SEN', 'SEL', 'RAB', 'KAM', 'JUM', 'SAB', 'MIN'];

const getDaysInMonthMatrix = (year: number, month: number) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let startDay = firstDay.getDay();
  startDay = startDay === 0 ? 6 : startDay - 1;
  const totalDays = lastDay.getDate();
  const matrix: (number | null)[] = [];
  for (let i = 0; i < startDay; i++) {
    matrix.push(null);
  }
  for (let d = 1; d <= totalDays; d++) {
    matrix.push(d);
  }
  return matrix;
};

const formatCustomDatePillLabel = (dateStr: string): string => {
  if (!dateStr) return 'Tanggal';
  try {
    const parts = dateStr.split('-');
    if (parts.length < 3) return 'Tanggal';
    const mIdx = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return `${d} ${monthsShort[mIdx] || ''}`;
  } catch {
    return 'Tanggal';
  }
};

// Helper to format Date Box
const formatDateBox = (dateStr: string): { day: string; month: string; dayName: string } => {
  try {
    const d = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    const days = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
    return {
      day: String(d.getDate()).padStart(2, '0'),
      month: months[d.getMonth()] || 'Jan',
      dayName: days[d.getDay()] || 'Sen',
    };
  } catch {
    return { day: '01', month: 'Jan', dayName: 'Sen' };
  }
};

// Helper to format Time range
const formatTimeRange = (start?: string, end?: string): string => {
  if (!start) return 'Waktu belum diatur';
  return formatLocalEventTimeRange(start, end) || 'Waktu belum diatur';
};

// Helper to format Currency
const formatCurrency = (val?: number): string => {
  if (!val || val === 0) return 'Gratis';
  return `Rp. ${val.toLocaleString('id-ID')}`;
};

const ClientDiscoveryEventPage: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<PublicMabarEvent[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDatePill, setSelectedDatePill] = useState<DateFilterPill>('today');
  const [customSelectedDate, setCustomSelectedDate] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');

  // Date Picker Bottom Sheet state
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());

  // Mode Change Bottom Sheet state
  const [isModeModalOpen, setIsModeModalOpen] = useState(false);
  const [isModeModalClosing, setIsModeModalClosing] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setEvents([]);

      // Compute date filters based on pill
      let date_from: string | undefined = undefined;
      let date_to: string | undefined = undefined;
      const now = new Date();

      if (selectedDatePill === 'today') {
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        date_from = `${yyyy}-${mm}-${dd}`;
        date_to = `${yyyy}-${mm}-${dd}`;
      } else if (selectedDatePill === 'weekend') {
        let sat = new Date(now);
        if (now.getDay() === 0) {
          sat.setDate(now.getDate() - 1);
        } else {
          sat.setDate(now.getDate() + ((6 - now.getDay() + 7) % 7));
        }
        const sun = new Date(sat);
        sun.setDate(sat.getDate() + 1);
        const yyyySat = sat.getFullYear();
        const mmSat = String(sat.getMonth() + 1).padStart(2, '0');
        const ddSat = String(sat.getDate()).padStart(2, '0');
        const yyyySun = sun.getFullYear();
        const mmSun = String(sun.getMonth() + 1).padStart(2, '0');
        const ddSun = String(sun.getDate()).padStart(2, '0');
        date_from = `${yyyySat}-${mmSat}-${ddSat}`;
        date_to = `${yyyySun}-${mmSun}-${ddSun}`;
      } else if (selectedDatePill === 'custom') {
        if (customSelectedDate) {
          date_from = customSelectedDate;
          date_to = customSelectedDate;
        }
      }

      const res = await mabarService.getPublicEvents({
        search: searchQuery || undefined,
        city: selectedLocation || undefined,
        date_from,
        date_to,
        limit: 20,
      });

      const rawEvents = res.data || [];
      const validUpcomingEvents = rawEvents.filter(
        (ev) => !isEventPast(ev.event_date, ev.start_time, ev.end_time)
      );

      setEvents(validUpcomingEvents);
    } catch (err) {
      console.error('Failed to load discovery events:', err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedDatePill, selectedLocation, customSelectedDate]);

  const handleSelectDatePill = (pillId: DateFilterPill) => {
    if (pillId === 'custom') {
      setIsDatePickerOpen(true);
      return;
    }
    if (selectedDatePill === pillId) return;
    setLoading(true);
    setEvents([]);
    setSelectedDatePill(pillId);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEvents();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedDatePill, selectedLocation, customSelectedDate, fetchEvents]);

  const closeModeModal = () => {
    if (isModeModalClosing) return;
    setIsModeModalClosing(true);
    setTimeout(() => {
      setIsModeModalOpen(false);
      setIsModeModalClosing(false);
    }, 280);
  };

  const handleSelectMode = (targetPath: string) => {
    closeModeModal();
    setTimeout(() => {
      navigate(targetPath);
    }, 200);
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-24 md:pb-12">
      {/* Desktop View */}
      <div className="hidden md:block max-w-6xl mx-auto px-6 pt-6 pb-8 space-y-6 animate-fade-in">
        {/* Top Header & Mode Switcher */}
        <div className="bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-orange-50 border border-orange-100 text-[#FC480D] flex items-center justify-center shrink-0">
              <Icon icon="ph:calendar-blank-bold" className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                MODE PENCARIAN
              </p>
              <h1 className="text-base font-bold text-[#0E1726] leading-tight">
                Cari event
              </h1>
            </div>
          </div>

          {/* Desktop Mode Navigation Pills */}
          <div className="flex items-center gap-1.5 bg-gray-100/80 p-1 rounded-2xl border border-gray-200/60">
            <button
              type="button"
              className="px-4 py-2 rounded-xl text-xs font-bold bg-white text-[#FC480D] shadow-2xs flex items-center gap-1.5 cursor-default"
            >
              <Icon icon="ph:calendar-blank-bold" className="w-4 h-4" />
              <span>Cari event</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/discovery/community')}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 hover:text-[#0E1726] hover:bg-white/60 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Icon icon="ph:users-three-bold" className="w-4 h-4" />
              <span>Cari komunitas</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/discovery/arena')}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 hover:text-[#0E1726] hover:bg-white/60 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Icon icon="ph:buildings-bold" className="w-4 h-4" />
              <span>Booking lapangan</span>
            </button>
          </div>
        </div>

        {/* Desktop Filter Toolbar */}
        <div className="bg-white rounded-2xl border border-[#E0E6ED] p-3.5 shadow-2xs flex flex-wrap items-center justify-between gap-3">
          {/* Search Input & Location Dropdown */}
          <div className="flex items-center gap-2.5 flex-1 min-w-[320px] max-w-xl">
            <div className="relative flex-1">
              <Icon
                icon="ph:magnifying-glass-bold"
                className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Cari event, olahraga atau venue..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 pl-9 pr-8 rounded-xl border border-[#E0E6ED] bg-gray-50/50 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:bg-white shadow-2xs transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="w-48 shrink-0">
              <LocationSearchDropdown
                value={selectedLocation}
                onChange={setSelectedLocation}
              />
            </div>
          </div>

          {/* Date Filter Pills */}
          <div className="flex items-center gap-2">
            {[
              { id: 'today', label: 'Hari ini' },
              { id: 'weekend', label: 'Akhir pekan' },
              { id: 'all', label: 'Semua' },
              {
                id: 'custom',
                label:
                  selectedDatePill === 'custom' && customSelectedDate
                    ? formatCustomDatePillLabel(customSelectedDate)
                    : 'Tanggal',
              },
            ].map((pill) => (
              <button
                key={pill.id}
                type="button"
                onClick={() => handleSelectDatePill(pill.id as DateFilterPill)}
                className={`h-11 px-4 rounded-xl text-xs font-bold shrink-0 transition-all duration-200 cursor-pointer shadow-2xs ${
                  selectedDatePill === pill.id
                    ? 'bg-[#FC480D] text-white shadow-orange-200/50'
                    : 'bg-white text-gray-700 border border-[#E0E6ED] hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                {pill.label}
              </button>
            ))}
          </div>
        </div>

        {/* Section Header */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2.5">
            <h2 className="text-base font-bold text-[#0E1726]">
              Cocok buat kamu
            </h2>
            {!loading && (
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-orange-50 text-[#FC480D] border border-orange-100">
                {events.length} event
              </span>
            )}
          </div>
          <span className="text-xs font-semibold text-gray-400">
            Peringkat · Aktif
          </span>
        </div>

        {/* Events Grid for Desktop */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <MabarCardSkeleton />
            <MabarCardSkeleton />
            <MabarCardSkeleton />
            <MabarCardSkeleton />
          </div>
        ) : events.length === 0 ? (
          <div key={selectedDatePill} className="bg-white rounded-3xl p-12 border border-[#E0E6ED] text-center space-y-2 shadow-2xs animate-in fade-in duration-300">
            <Icon icon="ph:calendar-x-bold" className="w-12 h-12 text-gray-300 mx-auto" />
            <h3 className="text-sm font-bold text-[#0E1726]">Belum Ada Event</h3>
            <p className="text-xs text-gray-400 max-w-xs mx-auto">
              Tidak ada event mabar yang cocok dengan kriteria filter Anda saat ini.
            </p>
          </div>
        ) : (
          <div key={selectedDatePill} className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-in fade-in duration-300">
            {events.map((event) => {
              const dateBox = formatDateBox(event.event_date);
              const priceVal = event.roles_availability?.[0]?.price || 0;
              const filled = event.filled_slots || 0;
              const total = event.total_slots || 1;
              const available = event.available_slots ?? (total - filled);
              const isLowSlots = available <= 3 && available > 0;
              const progressPct = Math.min(100, Math.round((filled / total) * 100));

              return (
                <div
                  key={event.slug}
                  onClick={() => navigate(`/event/${event.slug}`)}
                  className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs hover:shadow-md hover:border-orange-200 hover:-translate-y-0.5 transition-all space-y-3.5 cursor-pointer flex flex-col justify-between"
                >
                  <div className="space-y-3.5">
                    {/* Top Badges Row */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="bg-orange-50 border border-orange-100 text-[#FC480D] font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1.5">
                        <Icon icon="ph:soccer-ball-bold" className="w-3.5 h-3.5" />
                        <span>{event.sport_category?.name || 'Olahraga'}</span>
                      </span>

                      {isLowSlots ? (
                        <span className="bg-rose-50 border border-rose-100 text-rose-600 font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1">
                          <Icon icon="ph:fire-bold" className="w-3.5 h-3.5" />
                          <span>Cepat penuh</span>
                        </span>
                      ) : (
                        <span className="bg-emerald-50 border border-emerald-100 text-emerald-600 font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1">
                          <Icon icon="ph:check-circle-bold" className="w-3.5 h-3.5" />
                          <span>Jadwal cocok</span>
                        </span>
                      )}
                    </div>

                    {/* Main Details Grid */}
                    <div className="flex items-start gap-3.5">
                      {/* Date Box */}
                      <div className="w-14 h-16 rounded-2xl bg-orange-50/60 border border-orange-100/80 flex flex-col items-center justify-center shrink-0">
                        <span className="text-[11px] font-bold text-[#FC480D] uppercase leading-tight">
                          {dateBox.month}
                        </span>
                        <span className="text-xl font-extrabold text-[#0E1726] leading-none my-0.5">
                          {dateBox.day}
                        </span>
                        <span className="text-[10px] font-semibold text-gray-400 capitalize leading-tight">
                          {dateBox.dayName}
                        </span>
                      </div>

                      {/* Event Info */}
                      <div className="space-y-1.5 flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-[#0E1726] truncate">
                          {event.name}
                        </h3>

                        <div className="space-y-1 text-xs text-gray-500 font-medium">
                          <div className="flex items-center gap-1.5 truncate">
                            <Icon icon="ph:clock-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span>{formatTimeRange(event.start_time, event.end_time)}</span>
                          </div>

                          <div className="flex items-center gap-1.5 truncate">
                            <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="truncate">
                              {event.lokasi_arena ? `${event.lokasi_arena} - ${event.city || ''}` : event.city || 'Lokasi belum diatur'}
                            </span>
                          </div>

                          <div className="flex items-center gap-1.5">
                            <Icon icon="ph:tag-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="font-bold text-[#0E1726]">
                              {formatCurrency(priceVal)}{' '}
                              <span className="font-normal text-gray-400 text-[11px]">per orang</span>
                            </span>
                          </div>
                        </div>

                        {/* Slots Progress Bar */}
                        <div className="space-y-1 pt-1">
                          <div className="flex items-center justify-between text-[11px] font-semibold">
                            <span className="text-gray-400">{filled}/{total} terisi</span>
                            <span className="text-[#FC480D]">Sisa {available} slot</span>
                          </div>
                          <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#FC480D] rounded-full transition-all duration-300"
                              style={{ width: `${progressPct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Host & Action Footer */}
                  <div className="border-t border-gray-100 pt-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      {event.host?.avatar ? (
                        <img
                          src={event.host.avatar}
                          alt={event.host.name}
                          className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-100"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#FC480D] text-white font-bold text-xs flex items-center justify-center shrink-0">
                          {(event.host?.name || 'S').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="text-xs font-bold text-[#0E1726] truncate">
                        {event.host?.name || 'Sporto Community'}
                      </span>
                    </div>

                    {event.is_joined || event.isJoined || event.is_host ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/event/${event.slug}`);
                        }}
                        className="h-9 px-4 rounded-full bg-[#0E1726] hover:bg-[#1C2838] text-white text-xs font-bold shadow-2xs transition-all active:scale-98 cursor-pointer flex items-center gap-1.5 shrink-0"
                      >
                        <Icon icon="ph:eye-bold" className="w-3.5 h-3.5" />
                        <span>Detail</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/event/${event.slug}`);
                        }}
                        className="h-9 px-4 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-bold shadow-2xs transition-all active:scale-98 cursor-pointer flex items-center gap-1.5 shrink-0"
                      >
                        <Icon icon="ph:sign-in-bold" className="w-3.5 h-3.5" />
                        <span>Gabung</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        <div className="max-w-2xl mx-auto px-4 pt-4 md:pt-6 space-y-4 animate-fade-in">
        {/* Top Card: MODE PENCARIAN */}
        <div className="bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-orange-50 border border-orange-100 text-[#FC480D] flex items-center justify-center shrink-0">
              <Icon icon="ph:calendar-blank-bold" className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                MODE PENCARIAN
              </p>
              <h2 className="text-sm font-bold text-[#0E1726] leading-tight">
                Cari event
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsModeModalOpen(true)}
            className="flex items-center gap-1 text-xs font-bold text-[#FC480D] hover:text-[#D83B08] transition-colors py-1.5 px-3 rounded-full hover:bg-orange-50 cursor-pointer"
          >
            <span>Ganti</span>
            <Icon icon="ph:arrows-clockwise-bold" className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Controls Row: Search Input & Location Dropdown */}
        <div className="grid grid-cols-3 gap-2.5">
          <div className="col-span-2 relative flex items-center">
            <Icon
              icon="ph:magnifying-glass-bold"
              className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Cari event, olahraga atau v..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 pl-9 pr-3 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] shadow-2xs transition-colors"
            />
          </div>
          <LocationSearchDropdown
            value={selectedLocation}
            onChange={setSelectedLocation}
          />
        </div>

        {/* Date Filter Pills Row */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {[
            { id: 'today', label: 'Hari ini' },
            { id: 'weekend', label: 'Akhir pekan' },
            { id: 'all', label: 'Semua' },
            {
              id: 'custom',
              label:
                selectedDatePill === 'custom' && customSelectedDate
                  ? formatCustomDatePillLabel(customSelectedDate)
                  : 'Tanggal',
            },
          ].map((pill) => (
            <button
              key={pill.id}
              type="button"
              onClick={() => handleSelectDatePill(pill.id as DateFilterPill)}
              className={`h-9 px-4 rounded-full text-xs font-bold shrink-0 transition-all duration-200 ease-out active:scale-95 cursor-pointer shadow-2xs ${
                selectedDatePill === pill.id
                  ? 'bg-[#FC480D] text-white shadow-orange-200/50 scale-102'
                  : 'bg-white text-gray-700 border border-[#E0E6ED] hover:bg-gray-50 hover:border-gray-300'
              }`}
            >
              {pill.label}
            </button>
          ))}
        </div>

        {/* Section Header Title */}
        <div className="flex items-center justify-between pt-1">
          <h2 className="text-base font-bold text-[#0E1726]">
            Cocok buat kamu
          </h2>
          <span className="text-xs font-semibold text-gray-400">
            Peringkat · Aktif
          </span>
        </div>

        {/* Events List with Smooth Fade-in Transition */}
        {loading ? (
          <div className="space-y-4">
            <MabarCardSkeleton />
            <MabarCardSkeleton />
          </div>
        ) : events.length === 0 ? (
          <div key={selectedDatePill} className="bg-white rounded-3xl p-8 border border-[#E0E6ED] text-center space-y-2 shadow-2xs animate-in fade-in duration-300">
            <Icon icon="ph:calendar-x-bold" className="w-10 h-10 text-gray-300 mx-auto" />
            <h3 className="text-sm font-bold text-[#0E1726]">Belum Ada Event</h3>
            <p className="text-xs text-gray-400 max-w-xs mx-auto">
              Tidak ada event mabar yang cocok dengan kriteria filter Anda saat ini.
            </p>
          </div>
        ) : (
          <div key={selectedDatePill} className="space-y-4 animate-in fade-in duration-300">
            {events.map((event) => {
              const dateBox = formatDateBox(event.event_date);
              const priceVal = event.roles_availability?.[0]?.price || 0;
              const filled = event.filled_slots || 0;
              const total = event.total_slots || 1;
              const available = event.available_slots ?? (total - filled);
              const isLowSlots = available <= 3 && available > 0;
              const progressPct = Math.min(100, Math.round((filled / total) * 100));

              return (
                <div
                  key={event.slug}
                  onClick={() => navigate(`/event/${event.slug}`)}
                  className="bg-white rounded-3xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs hover:shadow-xs transition-shadow space-y-3.5 cursor-pointer"
                >
                  {/* Top Badges Row */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="bg-orange-50 border border-orange-100 text-[#FC480D] font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1.5">
                      <Icon icon="ph:soccer-ball-bold" className="w-3.5 h-3.5" />
                      <span>{event.sport_category?.name || 'Olahraga'}</span>
                    </span>

                    {isLowSlots ? (
                      <span className="bg-rose-50 border border-rose-100 text-rose-600 font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1">
                        <Icon icon="ph:fire-bold" className="w-3.5 h-3.5" />
                        <span>Cepat penuh</span>
                      </span>
                    ) : (
                      <span className="bg-emerald-50 border border-emerald-100 text-emerald-600 font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1">
                        <Icon icon="ph:check-circle-bold" className="w-3.5 h-3.5" />
                        <span>Jadwal cocok</span>
                      </span>
                    )}
                  </div>

                  {/* Main Details Grid */}
                  <div className="flex items-start gap-3.5">
                    {/* Date Box */}
                    <div className="w-14 h-16 rounded-2xl bg-orange-50/60 border border-orange-100/80 flex flex-col items-center justify-center shrink-0">
                      <span className="text-[11px] font-bold text-[#FC480D] uppercase leading-tight">
                        {dateBox.month}
                      </span>
                      <span className="text-xl font-extrabold text-[#0E1726] leading-none my-0.5">
                        {dateBox.day}
                      </span>
                      <span className="text-[10px] font-semibold text-gray-400 capitalize leading-tight">
                        {dateBox.dayName}
                      </span>
                    </div>

                    {/* Event Info */}
                    <div className="space-y-1.5 flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-[#0E1726] truncate">
                        {event.name}
                      </h3>

                      <div className="space-y-1 text-xs text-gray-500 font-medium">
                        <div className="flex items-center gap-1.5 truncate">
                          <Icon icon="ph:clock-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span>{formatTimeRange(event.start_time, event.end_time)}</span>
                        </div>

                        <div className="flex items-center gap-1.5 truncate">
                          <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span className="truncate">
                            {event.lokasi_arena ? `${event.lokasi_arena} - ${event.city || ''}` : event.city || 'Lokasi belum diatur'}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <Icon icon="ph:tag-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span className="font-bold text-[#0E1726]">
                            {formatCurrency(priceVal)}{' '}
                            <span className="font-normal text-gray-400 text-[11px]">per orang</span>
                          </span>
                        </div>
                      </div>

                      {/* Slots Progress Bar */}
                      <div className="space-y-1 pt-1">
                        <div className="flex items-center justify-between text-[11px] font-semibold">
                          <span className="text-gray-400">{filled}/{total} terisi</span>
                          <span className="text-[#FC480D]">Sisa {available} slot</span>
                        </div>
                        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#FC480D] rounded-full transition-all duration-300"
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Host & Action Footer */}
                  <div className="border-t border-gray-100 pt-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      {event.host?.avatar ? (
                        <img
                          src={event.host.avatar}
                          alt={event.host.name}
                          className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-100"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#FC480D] text-white font-bold text-xs flex items-center justify-center shrink-0">
                          {(event.host?.name || 'S').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="text-xs font-bold text-[#0E1726] truncate">
                        {event.host?.name || 'Sporto Community'}
                      </span>
                    </div>

                    {event.is_joined || event.isJoined || event.is_host ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/event/${event.slug}`);
                        }}
                        className="h-9 px-4 rounded-full bg-[#0E1726] hover:bg-[#1C2838] text-white text-xs font-bold shadow-2xs transition-all active:scale-98 cursor-pointer flex items-center gap-1.5 shrink-0"
                      >
                        <Icon icon="ph:eye-bold" className="w-3.5 h-3.5" />
                        <span>Detail</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/event/${event.slug}`);
                        }}
                        className="h-9 px-4 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-bold shadow-2xs transition-all active:scale-98 cursor-pointer flex items-center gap-1.5 shrink-0"
                      >
                        <Icon icon="ph:sign-in-bold" className="w-3.5 h-3.5" />
                        <span>Gabung</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>

      {/* Change Mode Bottom Sheet Popup Modal */}
      {isModeModalOpen && (
        <div
          className={`fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end justify-center transition-opacity duration-300 ease-in-out ${
            isModeModalClosing ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-in fade-in duration-200'
          }`}
          onClick={closeModeModal}
        >
          <div
            className={`w-full max-w-md bg-white rounded-t-3xl p-5 sm:p-6 flex flex-col gap-4 shadow-2xl transition-transform duration-300 ease-in-out ${
              isModeModalClosing
                ? 'translate-y-full'
                : 'translate-y-0 animate-in slide-in-from-bottom duration-300'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle bar */}
            <div className="w-12 h-1 rounded-full bg-gray-300 self-center -mt-1 mb-1" />

            <div className="space-y-1">
              <h2 className="text-lg sm:text-xl font-bold text-[#0E1726]">
                Pilih Mode Pencarian
              </h2>
              <p className="text-xs text-gray-400 font-medium">
                Pilih satu untuk melihat jenis penjelajahan lain
              </p>
            </div>

            <div className="space-y-3 pt-1">
              {[
                {
                  id: 'event',
                  title: 'Cari event',
                  subtitle: 'Mabar, sparing & turnamen di sekitarmu',
                  icon: 'ph:calendar-blank-bold',
                  bgColor: 'bg-orange-50 border-orange-100',
                  iconColor: 'text-[#FC480D]',
                  path: '/discovery/event',
                },
                {
                  id: 'community',
                  title: 'Cari komunitas',
                  subtitle: 'Gabung grup yang rutin main tiap pekan',
                  icon: 'ph:users-three-bold',
                  bgColor: 'bg-blue-50 border-blue-100',
                  iconColor: 'text-blue-600',
                  path: '/discovery/community',
                },
                {
                  id: 'booking',
                  title: 'Booking lapangan',
                  subtitle: 'Sewa lapangan sesuai jam & lokasi',
                  icon: 'ph:buildings-bold',
                  bgColor: 'bg-emerald-50 border-emerald-100',
                  iconColor: 'text-emerald-600',
                  path: '/discovery/arena',
                },
              ].map((opt) => (
                <div
                  key={opt.id}
                  onClick={() => handleSelectMode(opt.path)}
                  className="w-full p-4 rounded-2xl bg-white border border-[#E0E6ED] shadow-2xs flex items-center justify-between transition-all cursor-pointer group active:scale-[0.99]"
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${opt.bgColor} ${opt.iconColor}`}
                    >
                      <Icon icon={opt.icon} className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-[#0E1726] group-hover:text-[#FC480D] transition-colors truncate">
                        {opt.title}
                      </h3>
                      <p className="text-xs text-gray-400 font-medium truncate mt-0.5">
                        {opt.subtitle}
                      </p>
                    </div>
                  </div>
                  <Icon
                    icon="ph:caret-right-bold"
                    className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0"
                  />
                </div>
              ))}
            </div>

            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={closeModeModal}
                className="text-xs font-semibold text-gray-500 hover:text-[#0E1726] transition-colors py-2 px-4 rounded-full cursor-pointer"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Date Picker Modal */}
      {isDatePickerOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-xs transition-all">
          <div
            className="fixed inset-0"
            onClick={() => setIsDatePickerOpen(false)}
          />
          <div className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl z-10 animate-in slide-in-from-bottom sm:zoom-in-95 duration-200">
            {/* Drag handle indicator */}
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />

            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Pilih Tanggal</h3>
              <button
                type="button"
                onClick={() => setIsDatePickerOpen(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <Icon icon="ph:x-bold" className="w-4 h-4" />
              </button>
            </div>

            {/* Month Navigator */}
            <div className="flex items-center justify-between mb-4 px-2">
              <button
                type="button"
                onClick={() => {
                  if (calendarMonth === 0) {
                    setCalendarMonth(11);
                    setCalendarYear((y) => y - 1);
                  } else {
                    setCalendarMonth((m) => m - 1);
                  }
                }}
                className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <Icon icon="ph:caret-left-bold" className="w-5 h-5" />
              </button>
              <span className="font-bold text-base text-gray-900">
                {indonesianMonths[calendarMonth]} {calendarYear}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (calendarMonth === 11) {
                    setCalendarMonth(0);
                    setCalendarYear((y) => y + 1);
                  } else {
                    setCalendarMonth((m) => m + 1);
                  }
                }}
                className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <Icon icon="ph:caret-right-bold" className="w-5 h-5" />
              </button>
            </div>

            {/* Weekdays Header */}
            <div className="grid grid-cols-7 gap-1 text-center mb-2">
              {dayNamesIndonesian.map((day) => (
                <span key={day} className="text-xs font-bold text-gray-400 py-1">
                  {day}
                </span>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-2 text-center">
              {getDaysInMonthMatrix(calendarYear, calendarMonth).map((day, idx) => {
                if (!day) {
                  return <div key={`empty-${idx}`} />;
                }

                const monthStr = String(calendarMonth + 1).padStart(2, '0');
                const dayStr = String(day).padStart(2, '0');
                const dateFormatted = `${calendarYear}-${monthStr}-${dayStr}`;

                const isSelected = customSelectedDate === dateFormatted && selectedDatePill === 'custom';

                return (
                  <button
                    key={dateFormatted}
                    type="button"
                    onClick={() => {
                      setCustomSelectedDate(dateFormatted);
                      setSelectedDatePill('custom');
                      setIsDatePickerOpen(false);
                    }}
                    className={`h-10 rounded-xl font-bold text-sm flex items-center justify-center transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-[#FC480D] text-white shadow-md'
                        : 'bg-white border border-gray-200 text-gray-800 hover:border-orange-300 hover:bg-orange-50/50'
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDiscoveryEventPage;
